import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import { queryKeys } from '../query-keys';
import type {
  AdminUserQueryParams,
  AdminUser,
  AdminUserDetail,
  PaginatedResponse,
  MetricsOverview,
  UpdateUserRoleData,
} from '../types';

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch paginated list of users for admin management.
 */
async function getAdminUsers(params?: AdminUserQueryParams): Promise<PaginatedResponse<AdminUser>> {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.set('search', params.search);
  if (params?.role) searchParams.set('role', params.role);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.limit) searchParams.set('limit', params.limit.toString());

  const queryString = searchParams.toString();
  const url = `/admin/users${queryString ? `?${queryString}` : ''}`;

  return apiFetch<PaginatedResponse<AdminUser>>(url);
}

/**
 * Fetch detailed user information for admin view.
 */
async function getAdminUserDetail(userId: string): Promise<AdminUserDetail> {
  return apiFetch<AdminUserDetail>(`/admin/users/${userId}`);
}

/**
 * Fetch admin dashboard metrics.
 */
async function getAdminMetrics(): Promise<MetricsOverview> {
  return apiFetch<MetricsOverview>('/admin/metrics');
}

/**
 * Update a user's app role.
 */
async function updateUserRole(userId: string, data: UpdateUserRoleData): Promise<AdminUser> {
  return apiFetch<AdminUser>(`/admin/users/${userId}/role`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch paginated list of users for admin management.
 * Supports search, filtering, sorting, and pagination.
 */
export function useAdminUsers(params?: AdminUserQueryParams) {
  return useQuery({
    queryKey: queryKeys.admin.users(params),
    queryFn: () => getAdminUsers(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch detailed user information including households.
 */
export function useAdminUserDetail(userId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.admin.userDetail(userId),
    queryFn: () => getAdminUserDetail(userId),
    enabled: options?.enabled ?? !!userId,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch admin dashboard metrics.
 * Provides user, household, item, and activity statistics.
 */
export function useAdminMetrics() {
  return useQuery({
    queryKey: queryKeys.admin.metrics(),
    queryFn: getAdminMetrics,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });
}

/**
 * Hook to update a user's app role.
 * Invalidates user list and detail queries on success.
 */
export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateUserRoleData }) =>
      updateUserRole(userId, data),
    onSuccess: (updatedUser, { userId }) => {
      // Update the user detail cache
      queryClient.setQueryData(
        queryKeys.admin.userDetail(userId),
        (old: AdminUserDetail | undefined) => {
          if (!old) return old;
          return { ...old, appRole: updatedUser.appRole };
        }
      );

      // Invalidate user list to reflect role change
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.users() });

      // Invalidate metrics if role changed affects counts
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.metrics() });
    },
  });
}
